import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Phone, Mail, MapPin, Clock } from 'lucide-react';
import ObfuscatedEmail from './ObfuscatedEmail';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

// Fallback values used while the settings request is in flight or if the
// admin endpoint is unreachable. Email is intentionally NOT bundled (anti-scrape):
// it's populated dynamically from /api/admin/settings.
const DEFAULTS = {
  phone: '+33 1 45 85 81 76',
  email: '',
  address: 'Paris, France',
  hours: '',
};

const Footer = () => {
  const { t } = useTranslation();
  const [emailRevealed, setEmailRevealed] = useState(false);
  const [info, setInfo] = useState(DEFAULTS);

  // Pull the live values from the admin settings so changes made in
  // Admin → Apparence → "Informations du pied de page" actually surface here.
  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE}/admin/settings`)
      .then((r) => r.json())
      .then((data) => {
        if (cancelled) return;
        const a = (data && data.appearance) || {};
        setInfo({
          phone: a.footer_phone || DEFAULTS.phone,
          email: a.footer_email || DEFAULTS.email,
          address: a.footer_address || DEFAULTS.address,
          hours: a.footer_hours || '',
        });
      })
      .catch(() => { /* keep defaults */ });
    return () => { cancelled = true; };
  }, []);

  // Strip non-digit chars for the tel: link.
  const telHref = `tel:${info.phone.replace(/[^+0-9]/g, '')}`;

  return (
    <footer className="bg-black text-white">
      <div className="container mx-auto px-6 md:px-12 lg:px-24 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          <div>
            <h2 className="text-2xl font-extrabold tracking-tighter mb-4">
              <span className="text-white">PUZZLE</span>
              <span className="text-primary">VIDEO</span>
            </h2>
            <p className="text-gray-400 text-sm leading-relaxed">
              {t('footer.tagline')}
            </p>
          </div>

          <div>
            <h3 className="font-semibold mb-4 text-sm uppercase tracking-wider">{t('footer.categories')}</h3>
            <ul className="space-y-2 text-sm text-gray-400">
              <li><Link to="/catalogue?category=cameras" className="hover:text-primary transition-colors">Caméras</Link></li>
              <li><Link to="/catalogue?category=optiques" className="hover:text-primary transition-colors">Optiques</Link></li>
              <li><Link to="/catalogue?category=lumiere" className="hover:text-primary transition-colors">Lumière</Link></li>
              <li><Link to="/catalogue?category=machinerie" className="hover:text-primary transition-colors">Machinerie</Link></li>
              <li><Link to="/catalogue?category=son" className="hover:text-primary transition-colors">Son</Link></li>
              <li><Link to="/catalogue?category=consommables" className="hover:text-primary transition-colors">Consommables</Link></li>
            </ul>
          </div>

          <div>
            <h3 className="font-semibold mb-4 text-sm uppercase tracking-wider">{t('footer.services')}</h3>
            <ul className="space-y-2 text-sm text-gray-400">
              <li><Link to="/catalogue" className="hover:text-primary transition-colors">{t('catalogue.title')}</Link></li>
              <li><Link to="/panier" className="hover:text-primary transition-colors">{t('nav.cart')}</Link></li>
              <li><Link to="/compte" className="hover:text-primary transition-colors">{t('nav.account')}</Link></li>
              <li><Link to="/contact" className="hover:text-primary transition-colors">{t('footer.contact')}</Link></li>
            </ul>
          </div>

          <div>
            <h3 className="font-semibold mb-4 text-sm uppercase tracking-wider">{t('footer.contact')}</h3>
            <ul className="space-y-3 text-sm text-gray-400">
              <li className="flex items-center gap-3">
                <Phone className="w-4 h-4 text-primary flex-shrink-0" />
                <a href={telHref} className="hover:text-primary transition-colors" data-testid="footer-phone">{info.phone}</a>
              </li>
              {info.email && (
                <li className="flex items-center gap-3">
                  <Mail className="w-4 h-4 text-primary flex-shrink-0" />
                  <ObfuscatedEmail
                    email={info.email}
                    className="hover:text-primary transition-colors"
                    testId="footer-email"
                  />
                </li>
              )}
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                <span data-testid="footer-address">{info.address}</span>
              </li>
              {info.hours && (
                <li className="flex items-start gap-3">
                  <Clock className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                  {/* Force gray-400 on every descendant via !important so inline
                      color styles from the admin rich editor (e.g. dark text
                      picked for the white Contact page) never leak onto this
                      black footer and become unreadable. */}
                  <span
                    data-testid="footer-hours"
                    className="text-gray-400 [&_*]:!text-gray-400"
                    dangerouslySetInnerHTML={{ __html: info.hours }}
                  />
                </li>
              )}
            </ul>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-gray-800 text-center text-sm text-gray-500">
          <p>&copy; {new Date().getFullYear()} Puzzle Video. {t('footer.rights')}</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
